import React from 'react';
import { motion } from 'framer-motion';
import { getMasteryLabelInfo } from '../../learningLab/shared/masteryLabelInfo';
import { getGrowthStage } from '../../mastery/growthStages';
interface MasteryRingProps {
  /** Mastery value 0-100. Use this for preset-color mode. */
  value?: number;
  /** Mastery score 0-100. When provided, auto-resolves ring color via getMasteryLabelInfo. */
  score?: number;
  size?: number;
  strokeWidth?: number;
  /** Preset color (used when neither `score` nor `ringColor` is provided). */
  color?: 'indigo' | 'emerald' | 'rose' | 'amber';
  /** Explicit hex color for the ring stroke — overrides both `color` and score-based color. */
  ringColor?: string;
  /**
   * Legacy boolean: when false, hides the inner label (back-compat).
   * If true (default), behavior depends on `display`.
   */
  showLabel?: boolean;
  /**
   * What to render INSIDE the ring:
   * - `'percentage'` (default, back-compat): "76%"
   * - `'label'`: text label only ("Sterk")
   * - `'stage'`: emoji + tiny label ("🏆 Sterk")
   * - `'both'`: label on top, % underneath
   * - `'none'`: nothing inside the ring
   *
   * See docs/specs/MASTERY_DISPLAY_LANGUAGE_2026.md for migration guidance.
   */
  display?: 'percentage' | 'label' | 'stage' | 'both' | 'none';
  className?: string;
}
const COLOR_HEX: Record<string, string> = {
  indigo: '#6366F1',
  emerald: '#10B981',
  rose: '#F43F5E',
  amber: '#F59E0B'
};
/**
 * Unified mastery ring component.
 *
 * Supports two color modes:
 * 1. **Preset color:** `<MasteryRing value={80} color="emerald" />` (parent/teacher portals)
 * 2. **Score-based dynamic color:** `<MasteryRing score={65} />` (learner-facing)
 *
 * And five display modes via `display` prop (see DisplayProp docs above).
 *
 * Migration note: default `display='percentage'` preserves existing behavior.
 * New call-sites should prefer `'label'` or `'stage'` per their portal's rendering contract.
 */
export function MasteryRing({
  value,
  score,
  size = 48,
  strokeWidth = 4,
  color = 'indigo',
  ringColor,
  showLabel = true,
  display = 'percentage',
  className = ''
}: MasteryRingProps) {
  // Resolve the display value: score takes precedence if value is not provided
  const displayValue = value ?? score ?? 0;
  // Resolve stroke color: explicit ringColor > score-based > preset
  let resolvedColor: string;
  if (ringColor) {
    resolvedColor = ringColor;
  } else if (score !== undefined) {
    resolvedColor = getMasteryLabelInfo(score).ring;
  } else {
    resolvedColor = COLOR_HEX[color] || COLOR_HEX.indigo;
  }
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(100, Math.max(0, displayValue));
  const offset = circumference - progress / 100 * circumference;
  // Effective display: showLabel=false hides everything (legacy)
  const effectiveDisplay = showLabel ? display : 'none';
  const stage = getGrowthStage(displayValue);
  return <div className={`relative inline-flex items-center justify-center ${className}`} style={{
    width: size,
    height: size
  }} data-id="element-7673">
      <svg className="transform -rotate-90 w-full h-full" viewBox={`0 0 ${size} ${size}`} data-id="element-7674">
        {/* Background track */}
        <circle stroke="#E2E8F0" strokeWidth={strokeWidth} fill="transparent" r={radius} cx={size / 2} cy={size / 2} data-id="element-7675" />
        {/* Progress ring */}
        <motion.circle stroke={resolvedColor} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeLinecap="round" fill="transparent" r={radius} cx={size / 2} cy={size / 2} initial={{
        strokeDashoffset: circumference
      }} animate={{
        strokeDashoffset: offset
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-7676" />
      </svg>

      {effectiveDisplay !== 'none' && <div className="absolute inset-0 flex flex-col items-center justify-center text-center leading-none" data-id="element-7677">
          {effectiveDisplay === 'percentage' && <span className="text-xs font-black text-slate-700" data-id="element-7678">
              {Math.round(displayValue)}
              <span className="text-[10px] text-slate-400" data-id="element-7679">%</span>
            </span>}
          {effectiveDisplay === 'label' && <span className="text-[10px] font-bold text-slate-700 px-1 truncate max-w-full" data-id="element-7680">
              {stage.label}
            </span>}
          {effectiveDisplay === 'stage' && <>
              <span className="text-base leading-none" aria-hidden="true" data-id="element-7681">
                {stage.emoji}
              </span>
              {size >= 56 && <span className="text-[9px] font-bold text-slate-600 mt-0.5 px-1 truncate max-w-full" data-id="element-7682">
                  {stage.label}
                </span>}
            </>}
          {effectiveDisplay === 'both' && <>
              <span className="text-[10px] font-bold text-slate-700 leading-none" data-id="element-7683">
                {stage.label}
              </span>
              <span className="text-[9px] font-semibold text-slate-400 mt-0.5" data-id="element-7684">
                {Math.round(displayValue)}%
              </span>
            </>}
        </div>}
    </div>;
}